import {
  TiendaPerfectaReportRoute,
  TiendaPerfectaRouteStop,
} from 'models/TiendaPerfecta';

/**
 * La derivación pura de la card «Incidencias del día» (TSK-2315). No hay
 * endpoint nuevo: todo sale del mismo `route-report` que ya se consume. Vive
 * separado del componente porque son las reglas que hay que tocar si el
 * contrato cambia, y porque se pueden probar sin montar nada de UI.
 *
 * Sin husos horarios acá: `finishedAt` viaja crudo y el componente lo formatea
 * en el huso del USUARIO, igual que el resto de la vista.
 */

export interface IncidenceTeam {
  id: string;
  name: string;
}

export interface IncidenceEntry {
  /** Ruta de la parada: base de los «teams afectados» del resumen. */
  routeId: string;
  stopId: string | null;
  /** `stops[].label` con caída a `stops[].code`, que nunca falta. */
  label: string;
  reason: string;
  code: string;
  note: string | null;
  finishedAt: string | null;
  /** `null` cuando la ruta no tiene camioneta asignada (`van: null`). */
  team: IncidenceTeam | null;
  /** `null` para "sin zona": clave ausente o zona de otra company. */
  zone: string | null;
}

export interface IncidenceRankingItem {
  reason: string;
  count: number;
}

export interface IncidencesSummary {
  total: number;
  /** Rutas distintas con al menos una incidencia (un team = una ruta). */
  affectedRouteCount: number;
  ranking: IncidenceRankingItem[];
}

/**
 * Una parada con bypass ya cerrado por excepción. El type predicate narrows
 * `bypass` a no-nulo, así el `map` de abajo lo lee sin asertar nada.
 *
 * Constraint 1: SOLO paradas FINALIZADAS cerradas por excepción. Queda afuera
 * a propósito la incidencia de una parada que todavía no cerró: existe el
 * caso real del peoneta que reporta "Local cerrado" y aun así completa las
 * tareas, y contarla antes de tiempo da un contador que BAJA solo cuando la
 * parada cierra sin incidencia. Un tablero cuyo número baja sin que nadie
 * arregle nada deja de usarse.
 */
const isCountableIncidence = (
  stop: TiendaPerfectaRouteStop
): stop is TiendaPerfectaRouteStop & {
  bypass: NonNullable<TiendaPerfectaRouteStop['bypass']>;
} => stop.status === 'finished' && stop.bypass !== null;

/**
 * Constraint 3: `zone` se chequea por FALSY, nunca `zone === ''`. `undefined`
 * (el backend todavía no mergeó la zona) y `''` (zona de otra company) son la
 * misma "sin zona" para esta card.
 */
const resolveZone = (
  distributor: TiendaPerfectaReportRoute['distributor']
): string | null => distributor?.zone || null;

/**
 * Una entrada por PARADA, no por ruta: una ruta con cuatro incidencias
 * produce cuatro entradas y repite su team. Es lo que hace que el total de la
 * cabecera y la cantidad de filas del detalle coincidan siempre.
 */
export const getIncidenceEntries = (
  routes: TiendaPerfectaReportRoute[]
): IncidenceEntry[] => {
  const zone = (route: TiendaPerfectaReportRoute) =>
    resolveZone(route.distributor);

  const team = (route: TiendaPerfectaReportRoute): IncidenceTeam | null =>
    route.van ? { id: route.van.id, name: route.van.name } : null;

  return routes.flatMap((route) =>
    route.stops.filter(isCountableIncidence).map((stop) => ({
      routeId: route.routeId,
      stopId: stop.id,
      label: stop.label || stop.code,
      reason: stop.bypass.reason,
      code: stop.bypass.code,
      note: stop.bypass.note ?? null,
      finishedAt: stop.finishedAt,
      team: team(route),
      zone: zone(route),
    }))
  );
};

/**
 * Constraint 2 (TSK-2329): un team es una RUTA, no una camioneta. Se deduplica
 * por `routeId`, que es la clave estable de la ruta; una camioneta con dos
 * rutas con incidencia cuenta dos, y una ruta con cuatro incidencias cuenta
 * una. Incluye rutas sin camioneta (`van: null`).
 */
export const summarizeIncidences = (
  entries: IncidenceEntry[]
): IncidencesSummary => {
  const affectedRouteIds = new Set(entries.map((entry) => entry.routeId));

  const countByReason = new Map<string, number>();
  entries.forEach((entry) => {
    countByReason.set(entry.reason, (countByReason.get(entry.reason) ?? 0) + 1);
  });

  /**
   * Desempate alfabético por `reason` cuando el `count` empata. Sin esto dos
   * motivos con la misma cantidad quedan en orden de INSERCIÓN, y con el
   * long-poll refrescando cada tanto esas dos barras pueden intercambiarse
   * solas entre refrescos sin que la incidencia real haya cambiado.
   */
  const ranking = Array.from(countByReason.entries())
    .map(([reason, count]) => ({ reason, count }))
    .sort((a, b) => b.count - a.count || a.reason.localeCompare(b.reason));

  return {
    total: entries.length,
    affectedRouteCount: affectedRouteIds.size,
    ranking,
  };
};
